if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', function () {
    const partFilter = document.getElementById('filterPartNumber');
    const productList = document.getElementById('productList');
    const scrollSentinel = document.getElementById('scrollSentinel');
    const loadingIndicator = document.getElementById('loadingIndicator');
    const scrollContainer = document.getElementById('similarProductsScrollContainer');

    const container = document.getElementById("related-products-container");
    if (!container) return;

    let allProducts = [];
    let filteredProducts = [];
    let displayedCount = 0;
    let paginationState = {
        hasNextPage: false,
        endCursor: null,
        isLoading: false
    };
    let scrollObserver = null;
    let scrollCheckInterval = null;
    const PRODUCTS_PER_PAGE = 10;

// --- 1. Fetch products from Storefront API with pagination ---
    const STOREFRONT_API_URL = "/api/2025-07/graphql.json";
    const STOREFRONT_TOKEN = document.getElementById('storefrontToken')?.value || "";
    const query = `
query getSimilarProducts(
  $first: Int!,
  $after: String,
  $id: ID!
) {
  collection(id: $id) {        
    products(
      first: $first,
      after: $after
    ) {
      edges {
        node {
          id
          title
          handle
          featuredImage { url altText }
          variants(first: 1) {
            edges {
              node {
                id
                sku
                price { amount currencyCode }
              }
            }
          }
          cadUrl1: metafield(namespace: "custom", key: "cadfile_1") { value }
          cadUrl2: metafield(namespace: "custom", key: "cadfile_2") { value }
          cadUrl3: metafield(namespace: "custom", key: "cadfile_3") { value }
          cadUrl4: metafield(namespace: "custom", key: "cadfile_4") { value }
          cadUrl5: metafield(namespace: "custom", key: "cadfile_5") { value }
          cadUrl6: metafield(namespace: "custom", key: "cadfile_6") { value }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }  
}
`;


    const normalizeId = (gid) => gid ? gid.split("/").pop() : null;

    // Transform product node to our format
    function transformProduct(node) {
        const variant = node.variants.edges[0]?.node;
        return {
            id: node.id,
            title: node.title,
            handle: node.handle,
            featured_image: node.featuredImage?.url || "",
            sku: variant?.sku || "",
            price: variant?.price
                ? `$${Number(variant.price.amount).toFixed(2)}`
                : "",
            cad_url: [
                node.cadUrl1?.value || "",
                node.cadUrl2?.value || "",
                node.cadUrl3?.value || "",
                node.cadUrl4?.value || "",
                node.cadUrl5?.value || "",
                node.cadUrl6?.value || ""
            ],
            variant_id: normalizeId(variant?.id) || ""
        };
    }

    // Fetch a single page of products
    async function fetchProductsPage(first, after = null) {
        const collection_id = container?.getAttribute("data-collection-id") || "";
        if (!collection_id) return { products: [], hasNextPage: false, endCursor: null };

        try {
            const res = await fetch(STOREFRONT_API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN
                },
                body: JSON.stringify({
                    query,
                    variables: {
                        id: `gid://shopify/Collection/${collection_id}`,
                        first,
                        after
                    }
                })
            });

            const data = await res.json();
            const edges = data?.data?.collection?.products?.edges || [];
            const pageInfo = data?.data?.collection?.products?.pageInfo || {};

            const products = edges.map(({node}) => transformProduct(node));

            return {
                products,
                hasNextPage: pageInfo.hasNextPage || false,
                endCursor: pageInfo.endCursor || null
            };
        } catch (error) {
            console.error('Error fetching products:', error);
            return { products: [], hasNextPage: false, endCursor: null };
        }
    }

    // Fetch all products (used for filtering)
    async function fetchAllProducts() {
        let products = [];
        let hasNextPage = true;
        let after = null;

        while (hasNextPage) {
            const result = await fetchProductsPage(50, after);
            products = products.concat(result.products);
            hasNextPage = result.hasNextPage;
            after = result.endCursor;
        }

        return products;
    }


// --- 2. Initialize ---
    (async function init() {
        // Get current product ID from container
        const currentProductId = container?.getAttribute("data-current-id");
        const normalizedCurrentId = currentProductId ? normalizeId(currentProductId) : null;

        // Fetch first batch (fetch extra in case current product needs to be excluded)
        const fetchCount = PRODUCTS_PER_PAGE + 1;
        const firstPage = await fetchProductsPage(fetchCount);

        let initialProducts = firstPage.products;

        // Exclude current product from initial load
        if (normalizedCurrentId) {
            initialProducts = initialProducts.filter(p => {
                const productId = normalizeId(p.id);
                return productId !== normalizedCurrentId && productId !== null;
            });
        }

        // Limit to 10 products for initial display
        const productsToDisplay = initialProducts.slice(0, PRODUCTS_PER_PAGE);

        if (productsToDisplay.length === 0) {
            if (container) container.innerHTML = '';
            return;
        }

        // Store products and pagination state
        allProducts = initialProducts;
        filteredProducts = initialProducts;
        displayedCount = productsToDisplay.length;

        // Update pagination state
        // If we got the full fetchCount and there are more pages, or if we filtered out the current product
        // and still have enough, there might be more
        const hasMoreProducts = firstPage.hasNextPage ||
            (firstPage.products.length === fetchCount && initialProducts.length >= PRODUCTS_PER_PAGE);

        paginationState.hasNextPage = hasMoreProducts;
        paginationState.endCursor = firstPage.endCursor;

        // Display first 10 products immediately
        if (productList) {
            renderProducts(productsToDisplay);
            updateLoadingIndicator();
            // Observer will be set up in renderProducts after container is visible
        }
    })();

// --- 3. Render products ---
    function renderProducts(products, append = false) {
        if (!productList) return;
        if (products.length === 0 && !append) {
            productList.innerHTML = '<div class="no-results">No results found.</div>';
            return;
        }
        const template = container?.getAttribute("data-template") || "";

        const productHTML = products.map((card) => {
            const productId = normalizeId(card.id);
            if(template==="special"){
                return `
                <div class="product-card" data-product data-part-number="${card.sku || ''}">
                  <div class="pdp-finder-img test"><img src="${card.featured_image || '/cdn/shop/files/placeholder-plp.png'}" alt="${card.title}" loading="lazy"></div>
                  ${card.sku ? `<div class="pdp-finder-sku">${card.sku}</div>` : ''}
                  <div class="pdp-finder-title"><span title="${card.sku}" class="pdp-finder-sku-mobile">${card.sku}</span><a title="${card.title}" href="/products/${card.handle}"><strong>${card.title}</strong></a></div>
                  <div class="pdp-finder-price">                
                  </div>
                  <div>TBD</div>
                  <div>
                      <button type="button" class="add-to-cart-btn btn" onclick="window.location.href='/products/${card.handle}'">View Product</button>
                  </div>
                </div>
              `;
            }else {
                return `
                <div class="product-card" data-product data-part-number="${card.sku || ''}" data-product-id="${productId}">
                  <div class="pdp-finder-img"><img src="${card.featured_image || '/cdn/shop/files/placeholder-plp.png'}" alt="${card.title}" loading="lazy"></div>
                  ${card.sku ? `<div class="pdp-finder-sku">${card.sku}</div>` : ''}
                  <div class="pdp-finder-title"><span title="${card.sku}" class="pdp-finder-sku-mobile">${card.sku}</span><a title="${card.sku}" href="/products/${card.handle}"><strong>${card.title}</strong></a></div>
                  <div class="pdp-finder-price">
                    ${window.isLoggedIn === true || window.isLoggedIn === 'true' ? (window.isB2B === true || window.isB2B === 'true' ? card.price : ''):''}
                  </div>
                  <div>
                  <button type="button" class="btn open-cad-modal" data-product-id="${productId}">
                  Download CAD
                  <svg class="desktop-only" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#1f3f65" viewBox="0 0 256 256"><path d="M228,144v64a12,12,0,0,1-12,12H40a12,12,0,0,1-12-12V144a12,12,0,0,1,24,0v52H204V144a12,12,0,0,1,24,0Zm-108.49,8.49a12,12,0,0,0,17,0l40-40a12,12,0,0,0-17-17L140,115V32a12,12,0,0,0-24,0v83L96.49,95.51a12,12,0,0,0-17,17Z"></path></svg>
                  </button>
                  </div>
                  ${window.isLoggedIn === true || window.isLoggedIn === 'true' ? (
                    window.isB2B === true || window.isB2B === 'true'
                        ? `
                        <div class="pdp-finder-qty">
                          <input type="hidden" class="variant-id" value="${card.variant_id}">
                          <label><span>Qty</span><input type="number" class="quantity-input" value="1" min="1"></label>
                          <button type="button" class="add-to-cart-btn btn">Add to Cart</button>
                        </div>
                      `
                                    : `
                        <div>
                          <button type="button" class="add-to-cart-btn  btn" onclick="window.location.href='/pages/request-webstore-login'">Register Your Company</button>
                        </div>
                      `
                            ) : `
                    <div>
                      <button type="button" class="add-to-cart-btn btn" onclick="window.location.href='/account/login'">Login to View Pricing</button>
                    </div>
                  `}

                </div>
              `;
            }
        }).join('');

        if (append) {
            productList.insertAdjacentHTML('beforeend', productHTML);
        } else {
            productList.innerHTML = productHTML;
        }

        attachAddToCartListeners();
        setupCadModal();
        container.style.display = 'block';

        // Setup scroll observer after container is visible
        if (!append) {
            setTimeout(() => {
                setupScrollObserver();
            }, 100);
        }
    }

    function updateLoadingIndicator() {
        if (!loadingIndicator) return;
        const hasMore = displayedCount < filteredProducts.length || paginationState.hasNextPage;
        loadingIndicator.style.display = (hasMore && paginationState.isLoading) ? 'block' : 'none';
    }

    async function loadMoreProducts() {
        if (paginationState.isLoading) return;

        // If we have more products in filteredProducts array, use those first
        if (displayedCount < filteredProducts.length) {
            const nextBatch = filteredProducts.slice(displayedCount, displayedCount + PRODUCTS_PER_PAGE);
            if (nextBatch.length > 0) {
                renderProducts(nextBatch, true);
                displayedCount += nextBatch.length;
                updateLoadingIndicator();

                // After rendering, check if we need to load more immediately
                // This helps with rapid scrolling within the scroll container
                if (displayedCount < filteredProducts.length || paginationState.hasNextPage) {
                    // Small delay to allow DOM to update, then check if sentinel is still visible
                    setTimeout(() => {
                        if (scrollContainer && scrollSentinel) {
                            const containerRect = scrollContainer.getBoundingClientRect();
                            const sentinelRect = scrollSentinel.getBoundingClientRect();
                            const containerBottom = containerRect.bottom;
                            const sentinelTop = sentinelRect.top;

                            // Sentinel is still visible in scroll container or close, load more
                            if (sentinelTop <= containerBottom + 500 && !paginationState.isLoading) {
                                loadMoreProducts();
                            }
                        }
                    }, 100);
                }
                return;
            }
        }

        // Otherwise, fetch next page from API
        if (paginationState.hasNextPage && paginationState.endCursor) {
            paginationState.isLoading = true;
            updateLoadingIndicator();

            const result = await fetchProductsPage(PRODUCTS_PER_PAGE, paginationState.endCursor);

            if (result.products.length > 0) {
                // Get current product ID to exclude it
                const currentProductId = container?.getAttribute("data-current-id");
                const normalizedCurrentId = currentProductId ? normalizeId(currentProductId) : null;

                // Exclude current product
                let newProducts = result.products;
                if (normalizedCurrentId) {
                    newProducts = newProducts.filter(p => normalizeId(p.id) !== normalizedCurrentId);
                }

                // Check for duplicates - exclude products already in allProducts
                const existingProductIds = new Set(allProducts.map(p => normalizeId(p.id)));
                newProducts = newProducts.filter(p => !existingProductIds.has(normalizeId(p.id)));

                // Add to allProducts only if we have new unique products
                if (newProducts.length > 0) {
                    allProducts = allProducts.concat(newProducts);

                    // If there's an active filter, filter the new products
                    const partVal = normalizeValue(partFilter?.value || '');
                    if (partVal) {
                        const filteredNew = newProducts.filter(card => {
                            const part = normalizeValue(card.sku);
                            return part.includes(partVal);
                        });
                        filteredProducts = filteredProducts.concat(filteredNew);
                    } else {
                        filteredProducts = filteredProducts.concat(newProducts);
                    }
                }

                // Display the new products (only those not already displayed)
                if (newProducts.length > 0) {
                    // Double-check: exclude products that are already displayed
                    const displayedProductIds = new Set(
                        Array.from(productList.querySelectorAll('[data-product-id]'))
                            .map(el => el.getAttribute('data-product-id'))
                            .filter(id => id)
                    );

                    const productsToDisplay = newProducts.filter(p => {
                        const productId = normalizeId(p.id);
                        return !displayedProductIds.has(productId);
                    });

                    if (productsToDisplay.length > 0) {
                        renderProducts(productsToDisplay, true);
                        displayedCount += productsToDisplay.length;
                    }
                }
            }

            // Update pagination state
            paginationState.hasNextPage = result.hasNextPage;
            paginationState.endCursor = result.endCursor;
            paginationState.isLoading = false;
            updateLoadingIndicator();

            // Only hide sentinel and disconnect observer if we're absolutely sure there are no more products
            // Check both: no more pages AND we've displayed all filtered products
            const hasMoreToDisplay = displayedCount < filteredProducts.length;
            const hasMorePages = paginationState.hasNextPage;

            if (!hasMorePages && !hasMoreToDisplay) {
                // Truly no more products - hide sentinel and disconnect
                if (scrollSentinel) {
                    scrollSentinel.style.display = 'none';
                }
                if (scrollObserver) {
                    scrollObserver.disconnect();
                    scrollObserver = null;
                }
                if (scrollCheckInterval) {
                    clearInterval(scrollCheckInterval);
                    scrollCheckInterval = null;
                }
            } else {
                // Ensure sentinel is visible if there might be more products
                if (scrollSentinel && (hasMorePages || hasMoreToDisplay)) {
                    scrollSentinel.style.display = 'block';
                }
            }
        } else {
            // Check if we still have products in filteredProducts array
            const hasMoreToDisplay = displayedCount < filteredProducts.length;

            if (!hasMoreToDisplay && !paginationState.hasNextPage) {
                // No more products to load, hide sentinel
                if (scrollSentinel) {
                    scrollSentinel.style.display = 'none';
                }
                if (scrollObserver) {
                    scrollObserver.disconnect();
                    scrollObserver = null;
                }
                if (scrollCheckInterval) {
                    clearInterval(scrollCheckInterval);
                    scrollCheckInterval = null;
                }
            } else {
                // Keep sentinel visible if there might be more products
                if (scrollSentinel) {
                    scrollSentinel.style.display = 'block';
                }
            }
        }
    }

    // Check if we need to load more products based on scroll position
    function checkScrollAndLoad() {
        if (paginationState.isLoading) return;

        if (!scrollSentinel) {
            scrollSentinel = document.getElementById('scrollSentinel');
            if (!scrollSentinel) return;
        }

        if (!scrollContainer) return;

        const hasMoreToDisplay = displayedCount < filteredProducts.length;
        const hasMorePages = paginationState.hasNextPage;

        if (!hasMoreToDisplay && !hasMorePages) {
            // No more products, stop checking
            if (scrollCheckInterval) {
                clearInterval(scrollCheckInterval);
                scrollCheckInterval = null;
            }
            return;
        }

        // Check if sentinel is visible within the scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const sentinelRect = scrollSentinel.getBoundingClientRect();

        // Check if sentinel is within or near the bottom of the scroll container
        const containerBottom = containerRect.bottom;
        const sentinelTop = sentinelRect.top;
        const threshold = 200; // Start loading 200px before reaching sentinel

        const isNearBottom = sentinelTop <= containerBottom + threshold;

        if (isNearBottom && (hasMoreToDisplay || hasMorePages)) {
            loadMoreProducts();
        }
    }

    // Setup Intersection Observer for auto-loading on scroll
    function setupScrollObserver() {
        // Disconnect existing observer if any
        if (scrollObserver) {
            scrollObserver.disconnect();
            scrollObserver = null;
        }

        // Clear existing scroll check interval
        if (scrollCheckInterval) {
            clearInterval(scrollCheckInterval);
            scrollCheckInterval = null;
        }

        if (!scrollSentinel) {
            // Try to get sentinel again in case it wasn't available before
            scrollSentinel = document.getElementById('scrollSentinel');
            if (!scrollSentinel) return;
        }

        // Make sure sentinel is visible
        scrollSentinel.style.display = 'block';

        const observerOptions = {
            root: scrollContainer, // Use scroll container as root instead of viewport
            rootMargin: '200px', // Start loading 200px before reaching the sentinel
            threshold: 0.01
        };

        scrollObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !paginationState.isLoading) {
                    // Check if we have more products to display or more pages to fetch
                    const hasMoreToDisplay = displayedCount < filteredProducts.length;
                    const hasMorePages = paginationState.hasNextPage;

                    if (hasMoreToDisplay || hasMorePages) {
                        loadMoreProducts();
                    } else {
                        // No more products, hide sentinel and disconnect observer
                        if (scrollSentinel) {
                            scrollSentinel.style.display = 'none';
                        }
                        if (scrollObserver) {
                            scrollObserver.disconnect();
                            scrollObserver = null;
                        }
                        if (scrollCheckInterval) {
                            clearInterval(scrollCheckInterval);
                            scrollCheckInterval = null;
                        }
                    }
                }
            });
        }, observerOptions);

        scrollObserver.observe(scrollSentinel);

        // Also set up a backup scroll check for rapid scrolling
        // This checks every 500ms if we're near the bottom and need to load more
        scrollCheckInterval = setInterval(() => {
            checkScrollAndLoad();
        }, 500);
    }

// --- 4. Filters ---
    async function filterProducts() {
        const partVal = normalizeValue(partFilter.value);

        // If no filter, use allProducts
        if (!partVal) {
            filteredProducts = allProducts;
        } else {
            // Filter existing products
            filteredProducts = allProducts.filter(card => {
                const part = normalizeValue(card.sku);
                return part.includes(partVal);
            });

            // If we don't have enough filtered products and there are more pages, fetch more
            if (filteredProducts.length < PRODUCTS_PER_PAGE && paginationState.hasNextPage) {
                // Continue fetching from current cursor position
                const currentProductId = container?.getAttribute("data-current-id");
                const normalizedCurrentId = currentProductId ? normalizeId(currentProductId) : null;
                let after = paginationState.endCursor;
                let hasMore = paginationState.hasNextPage;

                while (hasMore && filteredProducts.length < PRODUCTS_PER_PAGE * 2) {
                    const result = await fetchProductsPage(50, after);

                    let newProducts = result.products;
                    if (normalizedCurrentId) {
                        newProducts = newProducts.filter(p => normalizeId(p.id) !== normalizedCurrentId);
                    }

                    // Add new products that aren't already in allProducts (prevent duplicates)
                    const existingIds = new Set(allProducts.map(p => normalizeId(p.id)));
                    const uniqueNewProducts = newProducts.filter(p => !existingIds.has(normalizeId(p.id)));

                    if (uniqueNewProducts.length > 0) {
                        allProducts = allProducts.concat(uniqueNewProducts);
                        // Filter the new products
                        const filteredNew = uniqueNewProducts.filter(card => {
                            const part = normalizeValue(card.sku);
                            return part.includes(partVal);
                        });
                        // Also check for duplicates in filteredProducts before adding
                        const filteredProductIds = new Set(filteredProducts.map(p => normalizeId(p.id)));
                        const uniqueFilteredNew = filteredNew.filter(p => !filteredProductIds.has(normalizeId(p.id)));
                        filteredProducts = filteredProducts.concat(uniqueFilteredNew);
                    }

                    hasMore = result.hasNextPage;
                    after = result.endCursor;

                    // Update pagination state
                    paginationState.hasNextPage = result.hasNextPage;
                    paginationState.endCursor = result.endCursor;

                    // Stop if we have enough filtered products
                    if (filteredProducts.length >= PRODUCTS_PER_PAGE * 2) break;
                }
            }
        }

        displayedCount = Math.min(PRODUCTS_PER_PAGE, filteredProducts.length);
        renderProducts(filteredProducts.slice(0, displayedCount));
        updateLoadingIndicator();

        // Reset scroll observer after filtering
        if (scrollSentinel) {
            scrollSentinel.style.display = 'block';
        }
        // Re-setup observer after filtering
        setTimeout(() => {
            setupScrollObserver();
        }, 100);
    }

    partFilter?.addEventListener('input', filterProducts);

// --- 5. Add to Cart ---
    function attachAddToCartListeners() {
        document.querySelectorAll('.add-to-cart-btn').forEach(button => {
            button.addEventListener('click', function () {
                const card = button.closest('[data-product]');
                const variantId = card.querySelector('.variant-id')?.value;
                const quantity = card.querySelector('.quantity-input')?.value || 1;
                const sections = 'cart-icon-bubble';
                const sectionsUrl = window.location.pathname;
                if (!variantId) return;

                button.disabled = true;
                button.textContent = 'Adding...';
                fetch(`/cart/add.js?sections=${sections}&sections_url=${sectionsUrl}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: variantId, quantity })
                })
                    .then(res => res.json())
                    .then(response => {
                        button.textContent = 'Added ✔';
                        // Update cart icon bubble without opening minicart
                        try {
                            const html = response?.sections?.['cart-icon-bubble'];
                            if (html) {
                                const doc = new DOMParser().parseFromString(html, 'text/html');
                                const newSection = doc.querySelector('.shopify-section') || doc.querySelector('#shopify-section-cart-icon-bubble');
                                const currentBubble = document.getElementById('cart-icon-bubble');
                                if (newSection && currentBubble) {
                                    currentBubble.innerHTML = newSection.innerHTML;
                                }
                            }
                        } catch (e) {
                            console.warn('Cart icon bubble update failed', e);
                        }
                        // Toast with product name
                        try {
                            let productName = response?.product_title || response?.title;
                            if (!productName && card) {
                                const titleEl = card.querySelector('.card__heading, .product-title, [data-product-title]');
                                if (titleEl) productName = titleEl.textContent.trim();
                            }
                            const toastMsg = productName ? `${productName} Added to Cart Successfully` : 'Added to cart successfully';
                            if (window.showToast) window.showToast(toastMsg);
                        } catch (e) {
                            if (window.showToast) window.showToast('Added to cart successfully');
                        }
                        setTimeout(() => {
                            button.textContent = 'Add to Cart';
                            button.disabled = false;
                        }, 1000);
                    })
                    .catch(err => {
                        console.error('Add to cart failed', err);
                        button.disabled = false;
                        button.textContent = 'Add to Cart';
                    });
            });
        });
    }

// --- 6. CAD Modal ---
    function setupCadModal() {
        const cadModal = document.getElementById('cadModal');
        if (!cadModal) return;
        const closeBtn = document.getElementById('closeCadModal');
        const cadDownloads = document.getElementById('cadDownloads');

        function openModal(links) {
            cadDownloads.innerHTML = '';
            const validLinks = (links || []).filter(link => link && link.trim() !== '');
            if (validLinks && validLinks.length > 0) {
                validLinks.forEach(link => {
                  const fileName = link.split("/").pop().replace(".zip", "");
                  cadDownloads.innerHTML += `
                    <div class="downloads-block">
                      <span>${fileName}</span>
                      <a href="${link}" download>Download</a>
                    </div>
                  `;
                });
            } else {
                cadDownloads.innerHTML = `<div>No CAD files available.</div>`;
            }
            cadModal.style.display = 'flex';
        }

        function closeModal() {
            cadModal.style.display = 'none';
        }

        document.querySelectorAll('.open-cad-modal').forEach((btn) => {
            btn.addEventListener('click', () => {
                const productId = btn.getAttribute('data-product-id');
                if (productId) {
                    const product = allProducts.find(p => normalizeId(p.id) === productId);
                    if (product) {
                        const cadLinks = product.cad_url ? product.cad_url : [];
                        openModal(cadLinks);
                    }
                }
            });
        });

        closeBtn?.addEventListener('click', closeModal);
        cadModal.addEventListener('click', e => {
            if (e.target === cadModal) closeModal();
        });
    }

    function normalizeValue(val) {
        return (val || '')
            .toLowerCase()
            .replace(/["'()]/g, '') // remove quotes and parentheses
            .trim();
    }

});

